import React, { useCallback, useMemo, useState } from 'react';
import { Box, Button, Divider, Tooltip, Typography } from '@mui/material';
import { useTranslation, type TFunction } from 'react-i18next';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import DirectionsWalkOutlinedIcon from '@mui/icons-material/DirectionsWalkOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import { useMytaverse } from '@providers/MytaverseProvider';
import { useMytaverseEvent } from '@core/dashboard/providers';

import { useEditorMode } from '../EditorModeContext';
import { useMapEditor } from '../MapEditorContext';
import { useMapEditorCatalog } from '../useMapEditorCatalog';
import {
  findCatalogElement,
  hasBuiltGeometry,
  isGeometryMisconfigured,
  isMissingFromKit,
  type MapEditorCatalogElement,
} from '../mapEditorCatalog';
import { finishableSlotsFor } from '../mapEditorFinishes';
import { moduleLengthMetres } from '../mapEditorPlan';
import {
  describeLandFailure,
  describeRefusalAdvice,
} from '../mapEditorRefusalText';
import { describeSpotCount } from '../mapEditorSpotText';
import {
  findSpotByUeIdentifier,
  getSpotThumbnailUrl,
  normalizeSpotContentType,
} from '../spotContent';
import {
  findRefusalFor,
  findStandingSpots,
  setEditorModeInUnreal,
  START_ROOM_KEY,
  type MapLayoutSpot,
} from '../mapEditorUnreal';
import { useSegmentPicker } from '../MapEditor/SegmentPickerHost';
import {
  RoomPicture,
  type MapEditorSurfaceTints,
} from '../MapEditor/PlanGlyph';
import { FinishesSection } from './FinishesSection';
import { useInspectorStyles } from './styles';

interface ISegmentInspectorProps {
  // Placement key of the segment being worked on. START_ROOM_KEY is the entrance, which is a segment
  // like any other here — selectable, swappable for any room in the kit, carrying its own frames and
  // its own surfaces.
  roomKey: string;
}

/**
 * The line under a segment's name: how long it is, and what is on its walls.
 *
 * @param element - The module standing there.
 * @param translate - i18next `t`.
 * @returns The line, with whichever halves this build can answer for.
 */
const describeModule = (
  element: MapEditorCatalogElement,
  translate: TFunction,
): string => {
  const metres = moduleLengthMetres(element);

  const parts = [
    metres === null
      ? ''
      : translate('editorMode.mapEditorLengthMetres', '{{metres}} m long', {
          metres,
        }),
    describeSpotCount(element, translate),
  ].filter((part) => !!part);

  return parts.join(' · ');
};

/**
 * Which surfaces of the drawn picture read as re-dressed.
 *
 * A shade rather than the material's own colour, deliberately. The finish registry describes a
 * material by its variant, its texture set and seventeen numbers; none of that is a colour this hub
 * can resolve, and inventing one would put a wrong picture in front of an author who cannot see the
 * real one yet. A stronger shade answers the question the drawing can honestly answer — which of
 * these three surfaces has something on it — and the tiles below name what.
 *
 * @param slots - Semantic surfaces this segment offers.
 * @param dressed - Surfaces carrying a finish, from this segment or from the layout default.
 * @returns Tints for the drawing.
 */
const surfaceTints = (
  slots: string[],
  dressed: Set<string>,
): MapEditorSurfaceTints => {
  const tintOf = (slot: string, base: string) =>
    slots.includes(slot) && dressed.has(slot)
      ? 'rgba(174, 182, 255, 0.22)'
      : base;

  return {
    wall: tintOf('wall', 'rgba(255, 255, 255, 0.05)'),
    floor: tintOf('floor', 'rgba(255, 255, 255, 0.04)'),
    ceiling: tintOf('ceiling', 'rgba(255, 255, 255, 0.022)'),
  };
};

/**
 * Names the frames a standing segment holds.
 *
 * Reports what the world has rather than what the element declares, which is the more useful answer
 * for a placement: a module placed twice is measured twice, and each copy addresses its own frames.
 *
 * @param spots - Frames reported for this placement, or undefined when none were reported.
 * @param translate - i18next `t`.
 * @returns A phrase for the section's subtitle.
 */
const describeFrames = (
  spots: MapLayoutSpot[] | undefined,
  translate: TFunction,
): string => {
  // Absent is not zero — it means this segment's level has not been read yet, or it is not standing
  // at all because the draft holding it has never been published.
  if (!spots) {
    return translate(
      'editorMode.mapEditorFramesUnreported',
      'not standing yet — publish to see its frames',
    );
  }

  if (!spots.length) {
    return translate('editorMode.mapEditorFramesNone', 'nothing on its walls');
  }

  return spots.length === 1
    ? translate('editorMode.mapEditorFramesSingular', '1 frame')
    : translate('editorMode.mapEditorFramesPlural', '{{total}} frames', {
        total: spots.length,
      });
};

/**
 * The segment panel: one segment of the walk, and everything about it.
 *
 * The redesign's central rule, and the one to hold on to if this is ever refactored again: **this
 * panel is only ever about one segment**. Nothing layout-wide lives here — the status, Publish and
 * Undo all are on the plaque under the Scene panel — because a Publish button inside a panel titled
 * with one room's name reads as publishing that room.
 *
 * The surfaces section at the bottom was very nearly lost when the panel was rebuilt around a
 * selected segment: the board for it stayed on the design canvas with nothing leading to it, and for
 * one revision there was no way to change a texture at all. If this panel is refactored again, that
 * is the section that goes missing quietly.
 *
 * @param props.roomKey - The segment. START_ROOM_KEY is the entrance.
 */
export const SegmentInspector = ({ roomKey }: ISegmentInspectorProps) => {
  const { t: translate } = useTranslation();
  const styles = useInspectorStyles();
  const {
    rooms,
    startElement,
    removeRoomAt,
    moveRoom,
    finishes,
    builtRooms,
    refusals,
    previewRefusals,
    isDirty,
  } = useMapEditor();
  const { elements: catalogElements } = useMapEditorCatalog();
  const { openForSegment } = useSegmentPicker();
  const { sendMessageToUnreal } = useMytaverse();
  const {
    currentGeometryStartRoom,
    editorModeState,
    brandingSpots,
    roomSpots,
    currentBrandingId,
    isBrandingModeEnabled,
    setSelectedCustomizationUeId,
    setSelectedCustomizationTag,
    setSelectedCustomizationSpotType,
    setSelectedCustomizationTargetSize,
  } = useMytaverseEvent();
  const { walkRequested, setWalkRequested, selectSpot } = useEditorMode();

  // Thumbnails whose image failed to load, keyed by frame. A dead URL falls back to the empty
  // frame rather than to the browser's broken-image glyph.
  const [failedThumbs, setFailedThumbs] = useState<Record<string, boolean>>({});

  const isEntrance = roomKey === START_ROOM_KEY;
  const index = rooms.findIndex((room) => room.key === roomKey);
  const elementId = isEntrance ? startElement : rooms[index]?.id ?? '';
  const element = elementId
    ? findCatalogElement(catalogElements, elementId)
    : undefined;
  const isMissing = isMissingFromKit(catalogElements, elementId);

  const handleChange = useCallback(() => {
    openForSegment(roomKey);
  }, [openForSegment, roomKey]);

  // Take me there. The editor stays up — the tree still works while the author is inside — and the
  // walk begins in *this* segment rather than at the map's front door, which is what the `roomKey` on
  // the wire is for. A build that does not read it walks the layout from the player start, so the
  // control degrades to "walk the layout" rather than doing nothing.
  //
  // The request is recorded because this control is drawn unconditionally and the way back is not:
  // the banner that carries it reads `EDITOR_MODE_STATE`, and a build that sends none would otherwise
  // leave the author walking with nothing to walk back out of. See EditorModeContext.walkRequested.
  const handleTakeMeThere = useCallback(() => {
    setWalkRequested(true);
    setEditorModeInUnreal(sendMessageToUnreal, true, 'walk', roomKey);
  }, [sendMessageToUnreal, roomKey, setWalkRequested]);

  // A dirty draft is described by the draft's own report and only by that one: the published report
  // was keyed against a different chain, and the author's edits have moved every row out from under
  // it. Same rule the Layout panel applies.
  const activeRefusals = isDirty ? previewRefusals : refusals;
  const refusal = findRefusalFor(activeRefusals, roomKey, index);

  // What this segment ran into, when the report says. Named because that is most of the advice.
  const neighbourName = useMemo(() => {
    if (refusal?.reason !== 'overlap' || !refusal.blockedById) {
      return '';
    }

    return (
      findCatalogElement(catalogElements, refusal.blockedById)?.displayName ??
      refusal.blockedById
    );
  }, [refusal, catalogElements]);

  // The entrance's frames come from the report directly; every other segment's come through the
  // draft. Two sources for one question, and the split is the geometry bridge's rather than this
  // panel's: only the lobby's *id* reaches the draft, because only the id is a choice the author
  // made, and `builtRooms` is the author's chain. So the standing lobby is never in there, and
  // asking `findStandingSpots` for it can only ever answer "not standing" — which it did, on a
  // lobby standing with eight frames, until this was read where the bridge always said it would be.
  //
  // Guarded on the element matching, because the draft can name a lobby that is not the one
  // standing: pick a new entrance and the old one is still in the world until Publish. Reporting
  // the standing lobby's frames under the new lobby's name would be the same wrong answer with
  // more confidence.
  const standingEntranceSpots =
    currentGeometryStartRoom && currentGeometryStartRoom.id === elementId
      ? currentGeometryStartRoom.spots
      : undefined;
  const spots = isEntrance
    ? standingEntranceSpots
    : findStandingSpots(builtRooms, roomKey);

  // What is *on* those frames. The same list and the same match the Spots section reads, so a
  // frame and its row in that section cannot disagree about the picture they are both describing:
  // the open configuration's own spots while one is open, the room's own otherwise.
  const contentSpots =
    currentBrandingId && isBrandingModeEnabled ? brandingSpots : roomSpots;

  // Opening a frame is opening the spot inspector on its id — the same panel, and the same create
  // path, an in-world click on this frame reaches.
  //
  // The id has to reach the customization state and not only the selection: `useCustomSpotsEditor`
  // reads `selectedCustomizationUeId` when it creates the record, so a frame opened without
  // setting it would write the author's picture onto whichever spot was selected last. Setting it
  // is also what moves the editor into the Spots focus, through UESelectionBridge.
  //
  // `selectSpot` is called here as well rather than left to that bridge: the bridge's effect is
  // keyed on the id *changing*, so coming back to a frame already opened once would otherwise do
  // nothing at all.
  const handleFrameSelect = useCallback(
    (spot: MapLayoutSpot) => {
      // Guarded rather than assumed: an unaddressable frame draws no button, and a click that got
      // here anyway must not open an inspector whose every save would land on the wrong spot.
      if (!spot.spotId) {
        return;
      }

      setSelectedCustomizationUeId(spot.spotId);
      setSelectedCustomizationTag(spot.spotId);
      setSelectedCustomizationSpotType(normalizeSpotContentType(spot.type));
      // The frame's own measurement, where the standing build reports one (UE cs:15082). A frame it
      // could not measure carries no pair at all, and null is then the honest answer — cleared
      // rather than left alone either way, or the panel would describe the last frame's shape over
      // this one.
      //
      // Passed through as reported, in the units UE sent. Nothing here converts them, because
      // nothing downstream reads them as a length any more: the chip names the *ratio*, which is
      // the one thing a world extent can say about what image belongs on this surface.
      setSelectedCustomizationTargetSize(
        spot.width && spot.height
          ? { width: spot.width, height: spot.height }
          : null,
      );
      selectSpot(spot.spotId);
    },
    [
      selectSpot,
      setSelectedCustomizationUeId,
      setSelectedCustomizationTag,
      setSelectedCustomizationSpotType,
      setSelectedCustomizationTargetSize,
    ],
  );

  // The surfaces this segment offers. Resolved from the catalog rather than from the geometry report
  // on purpose: the offer has to be answerable for a segment sitting in an unpublished draft, and a
  // preview spawn is never reported.
  //
  // Through finishableSlotsFor rather than off `element.finishSlots` directly, because that function
  // owns the order the surfaces are asked about in — walls first, then floor, then ceiling — and the
  // build lists them in whatever order it read them. Reading the raw field put whichever surface the
  // level happened to classify first under the author's cursor.
  const offeredSlots = useMemo(
    () => (elementId ? finishableSlotsFor([elementId], catalogElements) : []),
    [elementId, catalogElements],
  );
  const dressed = useMemo(() => {
    const entries = [...finishes.defaults, ...(finishes.rooms[roomKey] ?? [])];

    return new Set(entries.map((entry) => entry.slot));
  }, [finishes, roomKey]);

  // Whether the author is standing in *this* segment, and whether the last attempt to put them here
  // worked. Both read off the report rather than off what this side asked for: a request is not an
  // outcome, and the whole reason these fields exist is that for a while the hub had only the
  // request and reported it as success.
  //
  // Gated on `walkRequested` so this panel only ever narrates a landing **it** asked for. Without it,
  // walking into this segment on foot — where nobody asked for a landing — would be described by
  // whatever verdict the last teleport anywhere had left on the wire.
  const isStandingHere =
    editorModeState?.view === 'walk' &&
    editorModeState.currentRoomKey === roomKey;
  const landing = useMemo(() => {
    if (!walkRequested || editorModeState?.view !== 'walk') {
      return 'none' as const;
    }
    // Absent means one of two things and only the sender can tell them apart — see
    // MapEditorModeState.landed. Here the sender is us, and `walkRequested` says we asked, so absent
    // is "still going" rather than "not applicable". Reading it as failure would flash a refusal
    // during every successful walk.
    if (editorModeState.landed === undefined) {
      return 'pending' as const;
    }

    return editorModeState.landed ? ('landed' as const) : ('failed' as const);
  }, [walkRequested, editorModeState]);

  // A room this build's kit cannot describe (MV-2004), and therefore one there is nothing here to
  // draw, measure or re-finish — `element` below is undefined for exactly these ids, so this is
  // answered ahead of the empty state rather than inside the main panel.
  //
  // Its own branch because both sentences the empty state offers are about something else — a layout
  // that never named an entrance, and a segment that has been taken out of the walk — and the second
  // is the one wrong answer an author can act on: it sends them looking for a row that is still in
  // front of them, while the real fault is which build they are standing in.
  //
  // Change is the only control that still means anything, and it is the way out: the room has to be
  // swapped for one this build has. Everything else in the panel describes a segment that is
  // standing, and this one never will.
  if (isMissing) {
    return (
      <Box sx={styles.mapEditorEmpty}>
        <Box
          sx={styles.mapEditorMissingBanner}
          data-testid="map-editor-segment-missing"
        >
          <ReportProblemOutlinedIcon sx={styles.mapEditorMissingIcon} />
          <Typography sx={styles.mapEditorMissingText}>
            {translate(
              'editorMode.mapEditorSegmentMissing',
              'This room is not in the build being streamed. It was chosen on a different build, so it cannot be built here — swap it for a room this build has.',
            )}
          </Typography>
        </Box>
        <Button
          sx={styles.mapEditorSegmentChange}
          onClick={handleChange}
          startIcon={<SwapHorizOutlinedIcon />}
          data-testid="map-editor-segment-change"
        >
          {translate('editorMode.mapEditorSegmentChange', 'Change')}
        </Button>
      </Box>
    );
  }

  if (!element) {
    return (
      <Box sx={styles.mapEditorEmpty}>
        <Typography sx={styles.emptyHint}>
          {isEntrance
            ? translate(
                'editorMode.mapEditorEntranceIsMap',
                "This layout opens on the entrance the map itself bakes in. Change it to open in one of the kit's rooms instead.",
              )
            : translate(
                'editorMode.mapEditorSegmentGone',
                'That segment is no longer in the walk. Pick another one on the left.',
              )}
        </Typography>
        {isEntrance && (
          <Button
            sx={styles.mapEditorSegmentChange}
            onClick={handleChange}
            startIcon={<SwapHorizOutlinedIcon />}
            data-testid="map-editor-segment-change"
          >
            {translate('editorMode.mapEditorSegmentChange', 'Change')}
          </Button>
        )}
      </Box>
    );
  }

  const isMisconfigured = isGeometryMisconfigured(element);
  const isUnbuilt = !hasBuiltGeometry(element);

  return (
    <Box data-testid="map-editor-segment-inspector">
      {/* First, above the picture: a segment that was turned down is the answer to "why is this not
          in the world", and it has to be readable before the author starts looking for it. */}
      {refusal && (
        <Box sx={styles.mapEditorRefusedBanner}>
          <BlockOutlinedIcon sx={styles.mapEditorRefusedIcon} />
          <Typography sx={styles.mapEditorRefusedText}>
            {describeRefusalAdvice(refusal.reason, translate, neighbourName)}
          </Typography>
        </Box>
      )}

      <RoomPicture
        element={element}
        tints={surfaceTints(offeredSlots, dressed)}
      />

      <Box sx={styles.mapEditorSegmentHead}>
        <Box sx={styles.mapEditorSegmentText}>
          <Typography sx={styles.mapEditorSegmentName}>
            {element.displayName}
          </Typography>
          <Typography sx={styles.mapEditorSegmentMeta}>
            {describeModule(element, translate)}
          </Typography>
        </Box>
        {(isMisconfigured || isUnbuilt) && (
          <Tooltip
            title={
              isMisconfigured
                ? translate(
                    'editorMode.mapEditorGeometryMisconfigured',
                    'This build cannot load the art for it — you will get a grey box.',
                  )
                : translate(
                    'editorMode.mapEditorGeometryUnbuilt',
                    'No art built for it yet — you will get a grey box.',
                  )
            }
          >
            <WarningAmberOutlinedIcon sx={styles.mapEditorSegmentWarning} />
          </Tooltip>
        )}
        <Button
          sx={styles.mapEditorSegmentChange}
          onClick={handleChange}
          startIcon={<SwapHorizOutlinedIcon />}
          data-testid="map-editor-segment-change"
        >
          {translate('editorMode.mapEditorSegmentChange', 'Change')}
        </Button>
      </Box>

      {/* The active state is back, and it is read from `currentRoomKey` rather than from what this
          side asked for. That distinction is the whole history of this control: styling it off
          `view === 'walk'` alone marked *every* segment as the one being stood in the moment a walk
          began anywhere, and styling it off the request would be just as wrong — a build that
          ignores `roomKey` lands the author at the player start and would still light this up. */}
      <Button
        sx={[
          styles.mapEditorTakeMeThere,
          isStandingHere && styles.mapEditorTakeMeThereActive,
        ]}
        onClick={handleTakeMeThere}
        startIcon={<DirectionsWalkOutlinedIcon />}
        data-testid="map-editor-take-me-there"
      >
        {isStandingHere
          ? translate('editorMode.mapEditorYouAreHere', 'You are here')
          : translate('editorMode.mapEditorTakeMeThere', 'Take me there')}
      </Button>

      {/* What became of the last landing this panel asked for. Nothing at all until it asks, which is
          why the author is not told about a walk they took on foot.

          The point of this block is the middle case: before these fields existed the refusal was
          silent — the state came back saying `view: 'walk'`, so the banner rose and said the author
          was standing in the published layout, while the pawn had not moved. A control that reports
          success and does nothing is the worst failure of the three, and it shipped that way. */}
      {landing !== 'none' && landing !== 'landed' && (
        <Box
          sx={
            landing === 'pending'
              ? styles.mapEditorLandingPending
              : styles.mapEditorLandingFailed
          }
          data-testid={
            landing === 'pending'
              ? 'map-editor-landing-pending'
              : 'map-editor-landing-failed'
          }
        >
          {landing === 'pending' ? (
            <Typography sx={styles.mapEditorLandingText}>
              {translate(
                'editorMode.mapEditorLandingPending',
                'Taking you there…',
              )}
            </Typography>
          ) : (
            <>
              <BlockOutlinedIcon sx={styles.mapEditorRefusedIcon} />
              <Typography sx={styles.mapEditorLandingText}>
                {describeLandFailure(editorModeState?.landFailure, translate)}
              </Typography>
            </>
          )}
        </Box>
      )}

      {/* Reorder and remove, on the segment being worked on rather than on every row of the tree.
          A 360px row cannot carry three icons and a name, and the entrance has neither: it is not a
          position in the walk and it cannot be taken out of one. */}
      {!isEntrance && index >= 0 && (
        <Box sx={styles.mapEditorSegmentActions}>
          <Button
            sx={styles.mapEditorSegmentAction}
            onClick={() => moveRoom(index, -1)}
            disabled={index === 0}
            data-testid="map-editor-segment-up"
          >
            {translate('editorMode.mapEditorSegmentEarlier', 'Move earlier')}
          </Button>
          <Button
            sx={styles.mapEditorSegmentAction}
            onClick={() => moveRoom(index, 1)}
            disabled={index === rooms.length - 1}
            data-testid="map-editor-segment-down"
          >
            {translate('editorMode.mapEditorSegmentLater', 'Move later')}
          </Button>
          <Button
            sx={[styles.mapEditorSegmentAction, styles.mapEditorSegmentRemove]}
            onClick={() => removeRoomAt(index)}
            data-testid="map-editor-segment-remove"
          >
            {translate('editorMode.mapEditorSegmentRemove', 'Remove')}
          </Button>
        </Box>
      )}

      <Divider sx={styles.mapEditorSegmentDivider} />

      <Box sx={styles.mapEditorFrames}>
        <Box sx={styles.mapEditorFramesHead}>
          <Typography sx={styles.mapEditorFramesTitle}>
            {translate('editorMode.mapEditorFrames', 'Frames')}
          </Typography>
          <Typography sx={styles.mapEditorFramesCount}>
            {describeFrames(spots, translate)}
          </Typography>
        </Box>
        {!!spots?.length && (
          <>
            <Box
              sx={styles.mapEditorFrameGrid}
              data-testid="map-editor-frames-grid"
            >
              {spots.map((spot, frameIndex) => {
                // An empty spotId is UE saying this frame cannot be addressed on its own: a room
                // standing without a placement key composes no per-spot id, and the report then
                // carries the empty string for every frame in it. Read off the payload rather than
                // special-cased per module — which room that happens to be is UE's business, and
                // the answer has already changed once.
                const isAddressable = !!spot.spotId;
                const thumbKey = spot.spotId || `frame-${frameIndex}`;
                const record = findSpotByUeIdentifier(
                  contentSpots,
                  spot.spotId,
                );
                const thumbUrl = failedThumbs[thumbKey]
                  ? null
                  : getSpotThumbnailUrl(record);
                const title = !isAddressable
                  ? translate(
                      'editorMode.mapEditorFrameUnaddressable',
                      'This frame stands without an id of its own — publish the layout to edit it',
                    )
                  : thumbUrl
                  ? translate(
                      'editorMode.mapEditorFrameFilled',
                      'Click to change what is on this frame',
                    )
                  : translate(
                      'editorMode.mapEditorFrameEmpty',
                      'Empty — click to put an image or video on it',
                    );

                return (
                  <Tooltip key={thumbKey} title={title}>
                    {isAddressable ? (
                      <Box
                        component="button"
                        type="button"
                        aria-label={title}
                        onClick={() => handleFrameSelect(spot)}
                        sx={[
                          styles.mapEditorFrameTile,
                          styles.mapEditorFrameTileButton,
                        ]}
                        data-testid={`map-editor-frame-tile-${spot.spotId}`}
                      >
                        {thumbUrl && (
                          <Box
                            component="img"
                            src={thumbUrl}
                            alt=""
                            sx={styles.mapEditorFrameThumb}
                            data-testid={`map-editor-frame-tile-${spot.spotId}-thumb`}
                            onError={() => {
                              setFailedThumbs((prev) => ({
                                ...prev,
                                [thumbKey]: true,
                              }));
                            }}
                          />
                        )}
                      </Box>
                    ) : (
                      <Box
                        sx={[
                          styles.mapEditorFrameTile,
                          styles.mapEditorFrameTileInert,
                        ]}
                        data-testid={`map-editor-frame-tile-unaddressable-${frameIndex}`}
                      />
                    )}
                  </Tooltip>
                );
              })}
            </Box>
            {/* Says what a click does, and that the wall catches up on its own. The pause is real
                and worth naming: SPOTS_UPDATED carries no media, it asks UE to re-fetch the event
                whole, so a frame repaints an HTTP round trip later rather than at once. An author
                told nothing would read that pause as a click that missed. */}
            <Typography
              sx={styles.mapEditorFramesHint}
              data-testid="map-editor-frames-hint"
            >
              {translate(
                'editorMode.mapEditorFramesHint',
                'Click a frame to set what is on it. It appears on the wall a moment later.',
              )}
            </Typography>
            {/* Only when there is one to explain. Every layout this hub publishes carries keys, so a
                caveat about the state it cannot be in would be noise on every other segment. */}
            {spots.some((spot) => !spot.spotId) && (
              <Typography
                sx={styles.mapEditorFramesHint}
                data-testid="map-editor-frames-unaddressable-hint"
              >
                {translate(
                  'editorMode.mapEditorFramesUnaddressableHint',
                  'Dimmed frames stand without an id of their own — publish the layout to give them one.',
                )}
              </Typography>
            )}
          </>
        )}
      </Box>

      <Divider sx={styles.mapEditorSegmentDivider} />

      <FinishesSection roomKey={roomKey} offeredSlots={offeredSlots} />
    </Box>
  );
};

export default SegmentInspector;
